import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Modal from '../ui/Modal'
import Button from '../ui/Button'

export default function PartnerStatsModal({ isOpen, onClose, partner }) {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isOpen && partner) {
      setLoading(true)
      fetch(`/collaboration/partners/${partner.id}/stats`)
        .then(res => res.json())
        .then(data => {
          setStats(data)
          setLoading(false)
        })
        .catch(() => setLoading(false))
    }
  }, [isOpen, partner])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Statistiques - ${partner.name}`} size="lg">
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des statistiques...</p>
        </div>
      ) : stats ? (
        <div className="space-y-6">
          {/* Global stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">{stats.total_matches || 0}</div>
              <div className="text-sm text-gray-600">Matches totaux</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{stats.successful_matches || 0}</div>
              <div className="text-sm text-gray-600">Matches réussis</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.success_rate || 0}%</div>
              <div className="text-sm text-gray-600">Taux de succès</div>
            </div>
          </div>

          {/* Revenue */}
          {stats.total_revenue && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-2">Revenue</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Revenue total</div>
                  <div className="text-lg font-semibold">{stats.total_revenue}€</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Revenue moyen par match</div>
                  <div className="text-lg font-semibold">{stats.average_revenue || 0}€</div>
                </div>
              </div>
            </div>
          )}

          {/* Recent matches */}
          {stats.recent_matches && stats.recent_matches.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-2">Matches récents</h3>
              <div className="space-y-2">
                {stats.recent_matches.slice(0, 5).map((match, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="text-sm">
                      <span className="font-medium">{match.lead_name || 'Lead'}</span>
                      <span className="text-gray-500 ml-2">
                        {new Date(match.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      match.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      match.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {match.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onClose}>
              Fermer
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-600">
          Aucune statistique disponible
        </div>
      )}
    </Modal>
  )
}

PartnerStatsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  partner: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired
}
